import type { IParkingLotCallResponse, IParkingLotCallsResponse, IParkingLotWithOccupancyResponse } from '@/openapi-clients-generated/talk';

export interface OccupancyCell {
    text: string;
    /** `ok` when calls are parked, `muted` for a known-empty lot, `unknown` when there is no count. */
    tone: 'ok' | 'muted' | 'unknown';
    tooltip?: string;
}

export const OCCUPANCY_UNAVAILABLE_TOOLTIP = 'Occupancy is temporarily unavailable';
export const OCCUPANCY_UNSUPPORTED_TOOLTIP = 'Not available on this voice backend';

/**
 * The list column for a lot's occupancy. A null count is never rendered as zero: an unreadable
 * store and a legacy backend each say so, rather than passing as an empty lot.
 */
export function occupancyCell(lot: Pick<IParkingLotWithOccupancyResponse, 'parkedCallCount' | 'occupancyStatus' | 'slotCount'>): OccupancyCell {
    if (lot.occupancyStatus === 'unsupported') return { text: '—', tone: 'unknown', tooltip: OCCUPANCY_UNSUPPORTED_TOOLTIP };
    if (lot.occupancyStatus === 'unavailable' || lot.parkedCallCount === null) {
        return { text: '—', tone: 'unknown', tooltip: OCCUPANCY_UNAVAILABLE_TOOLTIP };
    }
    return {
        text: `${lot.parkedCallCount} of ${lot.slotCount}`,
        tone: lot.parkedCallCount > 0 ? 'ok' : 'muted'
    };
}

export interface ParkingDockIndicator {
    /** The badge: the parked count, or an em dash when there is no count to show. */
    text: string;
    /** `attention` when calls are parked, `muted` for a known-empty lot, `unknown` when there is no count. */
    tone: 'attention' | 'muted' | 'unknown';
    tooltip: string;
}

/**
 * The sidebar dock's per-lot badge. Like `occupancyCell`, a null count is never shown as zero — an
 * unreadable store reads as "—" with the reason in the tooltip.
 */
export function parkingDockIndicator(lot: Pick<IParkingLotWithOccupancyResponse, 'parkedCallCount' | 'occupancyStatus'>): ParkingDockIndicator {
    if (lot.occupancyStatus === 'unsupported') return { text: '—', tone: 'unknown', tooltip: OCCUPANCY_UNSUPPORTED_TOOLTIP };
    if (lot.occupancyStatus === 'unavailable' || lot.parkedCallCount === null) {
        return { text: '—', tone: 'unknown', tooltip: OCCUPANCY_UNAVAILABLE_TOOLTIP };
    }
    if (lot.parkedCallCount === 0) return { text: '0', tone: 'muted', tooltip: 'No parked calls' };
    return {
        text: String(lot.parkedCallCount),
        tone: 'attention',
        tooltip: `${lot.parkedCallCount} parked call${lot.parkedCallCount === 1 ? '' : 's'}`
    };
}

/**
 * The call that has waited longest — what "Retrieve oldest" retrieves, through the same exact
 * per-call path as a row's Retrieve button. Ordered by `parkedAt` rather than trusting the
 * snapshot's row order, which follows slot numbers.
 */
export function oldestParkedCall(snapshot: Pick<IParkingLotCallsResponse, 'items'> | undefined): IParkingLotCallResponse | undefined {
    if (!snapshot?.items.length) return undefined;
    return snapshot.items.reduce((oldest, item) => (item.parkedAt < oldest.parkedAt ? item : oldest));
}

/** Whether the per-lot parked-calls read is worth offering at all. */
export function canListParkedCalls(lot: Pick<IParkingLotWithOccupancyResponse, 'occupancyStatus'>): boolean {
    return lot.occupancyStatus !== 'unsupported';
}
